import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import produce from 'immer';
import { useMemo, VFC } from 'react';

import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import { ArrayFieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ArrayFieldTemplate';
import { FieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/FieldTemplate';
import { HandlebarsField } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/HandlebarsField';
import { HandlebarsWidget } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/HandlebarsWidget';
import { ObjectFieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ObjectFieldTemplate';
import { PDSCheckBoxField } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/PDSCheckBoxField';
import { SelectWidget } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/SelectWidget';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import { WorkflowConfigError, WorkflowError, WorkflowErrorType } from 'dashboard/models/workflow';
import {
  HttpPlatformIntegrationConfig,
  WorkflowActionRunHttpPlatformIntegrationV1,
} from 'dashboard/models/workflow-version';
import { useGetExternalIntegrationVendorQuery } from 'dashboard/redux/api/external-integration-vendor';
import { SelectField } from 'design-system/components/FormField';
import { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';

interface ParamsRunHttpPlatformIntegrationV1Props
  extends ParamsBaseProps<WorkflowActionRunHttpPlatformIntegrationV1> {
  config: HttpPlatformIntegrationConfig;
  editable: boolean;
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
}

const ParamsRunHttpPlatformIntegrationV1: VFC<ParamsRunHttpPlatformIntegrationV1Props> = ({
  step,
  config,
  usableObjects,
  errors,
  updateStep,
  editable,
}) => {
  const errorsForStep = errors.filter(
    (error): error is WorkflowConfigError =>
      (error.type === WorkflowErrorType.Publish || error.type === WorkflowErrorType.Save) &&
      error.token === step.token,
  );

  const integrationConfigurationError = errorsForStep.find(
    (error) => error.field === 'integration-config',
  );

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const {
    integration,
    request: { schema },
  } = config;

  const updateIntegrationCredential = (credentialValue: string) =>
    updateStep(
      produce(step, (draft) => {
        draft.action.params.integration_credential = credentialValue;
      }),
    );

  const {
    data: {
      'reusable-elements': reusableElements = {} as ExternalIntegrationVendor['reusable-elements'],
    } = {},
    isLoading: isLoadingVendor,
  } = useGetExternalIntegrationVendorQuery(integration.vendor);

  const combinedSchema = useMemo(() => {
    if (Object.keys(schema).length > 0) {
      return {
        ...schema,
        ...reusableElements,
      };
    } else {
      return schema;
    }
  }, [schema, reusableElements]);

  return (
    <>
      <Spacer height={2} />
      <SelectField
        label='Integration Credential'
        placeholder='Select a Credential'
        onValueChange={updateIntegrationCredential}
        value={step.action.params.integration_credential}
        errorText={integrationConfigurationError?.message}
      >
        {config['integration-configs'].map((config) => (
          <Option key={config.value} value={config.value}>
            {config.label}
          </Option>
        ))}
      </SelectField>
      {isLoadingVendor && <LoadingIndicator />}
      {!isLoadingVendor &&
        Object.keys(config.request.parametersSchema?.properties || {}).length > 0 && (
          <Form
            id={`form-parameters-${integration.vendor}-${integration['operation_id']}`}
            idPrefix={`params-parameters-${integration.vendor}-${integration['operation_id']}`}
            idSeparator='__'
            css={{
              // The <Form /> element has a 16px spacer that we should offset.
              marginBottom: -16,
              paddingBottom: 0,
            }}
            formData={step.action.params.request_parameters}
            onChange={(data, _) => {
              updateStep(
                produce(step, (draft) => {
                  draft.action.params.request_parameters = data.formData;
                  draft.action.params.integration = integration;
                }),
              );
            }}
            schema={config.request.parametersSchema}
            uiSchema={{
              ...config.request.parametersUiSchema,
              'ui:submitButtonOptions': { norender: true },
            }}
            validator={validator}
            fields={{
              BooleanField: PDSCheckBoxField,
              handlebars: HandlebarsField,
            }}
            widgets={{
              TextWidget: HandlebarsWidget,
              DateWidget: HandlebarsWidget,
              DateTimeWidget: HandlebarsWidget,
              SelectWidget,
            }}
            templates={{
              FieldTemplate,
              ObjectFieldTemplate,
              ArrayFieldTemplate,
            }}
            formContext={{
              jsonSchema: jsonSchema,
              tokensToDisplayValue: tokensToDisplayValue,
              errorsForStep,
            }}
            showErrorList={false}
            disabled={!editable}
          />
        )}
      {!isLoadingVendor && (
        <Form
          id={`form-${integration.vendor}-${integration['operation_id']}`}
          idPrefix={`params-${integration.vendor}-${integration['operation_id']}`}
          idSeparator='__'
          formData={step.action.params.request_body}
          onChange={(data, _) => {
            updateStep(
              produce(step, (draft) => {
                draft.action.params.request_body = data.formData;
                draft.action.params.integration = integration;
              }),
            );
          }}
          schema={combinedSchema}
          uiSchema={{
            ...config.request.uiSchema,
            'ui:submitButtonOptions': { norender: true },
          }}
          validator={validator}
          fields={{
            BooleanField: PDSCheckBoxField,
            handlebars: HandlebarsField,
          }}
          widgets={{
            TextWidget: HandlebarsWidget,
            DateWidget: HandlebarsWidget,
            DateTimeWidget: HandlebarsWidget,
            SelectWidget,
          }}
          templates={{
            FieldTemplate,
            ObjectFieldTemplate,
            ArrayFieldTemplate,
          }}
          formContext={{
            jsonSchema: jsonSchema,
            tokensToDisplayValue: tokensToDisplayValue,
            errorsForStep,
          }}
          showErrorList={false}
          disabled={!editable}
        />
      )}
    </>
  );
};

export default ParamsRunHttpPlatformIntegrationV1;
